import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { imgUrl, useThemes } from "@/lib/inspiration";
import { useSeo } from "@/lib/site";

const ThemeEditor = ({ t, refresh }) => {
  const [f, setF] = useState({ name: t.name, description: t.description, order: t.order });
  const [caption, setCaption] = useState("");
  useEffect(() => setF({ name: t.name, description: t.description, order: t.order }), [t]);
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const upload = (path, file, extra = {}) => {
    const fd = new FormData(); fd.append("file", file);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return api.post(path, fd);
  };
  const id = t.slug;
  return (
    <section data-testid={`admin-theme-${id}`} className="grid gap-6 border border-[#080808]/10 bg-white p-6 lg:grid-cols-[160px_1fr]">
      <div className="space-y-2">
        <img src={imgUrl(t.cover.url)} alt="" className="aspect-[3/4] w-full rounded object-cover" />
        <label className="block cursor-pointer text-center text-xs underline underline-offset-4">Cambiar portada
          <input type="file" accept="image/*" hidden data-testid={`admin-theme-${id}-cover-input`}
            onChange={(e) => e.target.files[0] && run(() => upload(`/admin/inspiration/${id}/cover`, e.target.files[0]), "Portada actualizada")} /></label>
      </div>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr_80px]">
          <Input data-testid={`admin-theme-${id}-name`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input data-testid={`admin-theme-${id}-description`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <Input data-testid={`admin-theme-${id}-order`} type="number" min={0} value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
        </div>
        <Button data-testid={`admin-theme-${id}-save`} className="rounded-full" onClick={() => run(() => api.put(`/admin/inspiration/${id}`, f), "Temática guardada")}>Guardar</Button>
        <div className="flex flex-wrap gap-3">
          {t.examples.map((e) => (
            <div key={e.id} className="relative w-24">
              <img src={imgUrl(e.url)} alt="" className="aspect-square w-full rounded object-cover" />
              <button type="button" aria-label="Eliminar ejemplo" data-testid={`admin-example-${e.id}-delete`} onClick={() => run(() => api.delete(`/admin/inspiration/${id}/examples/${e.id}`), "Ejemplo eliminado")}
                className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-white/90 hover:bg-[#B18A4B]"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input placeholder="Descripción del ejemplo (opcional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="max-w-xs" data-testid={`admin-theme-${id}-caption`} />
          <label className="cursor-pointer rounded-full border border-[#080808]/20 px-4 py-2 text-xs hover:border-[#080808]">Agregar ejemplo
            <input type="file" accept="image/*" hidden data-testid={`admin-theme-${id}-example-input`}
              onChange={(e) => e.target.files[0] && run(() => upload(`/admin/inspiration/${id}/examples`, e.target.files[0], { caption }).then(() => setCaption("")), "Ejemplo agregado")} /></label>
        </div>
      </div>
    </section>
  );
};

export default function InspirationAdmin() {
  useSeo({ title: "Inspiración · Administración", noindex: true });
  const qc = useQueryClient();
  const { data = [] } = useThemes();
  return (
    <div className="min-h-screen bg-[#FAF8F4] px-5 sm:px-10 py-10">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-4xl">Inspírate con LUMINA</h1>
        <Link to="/studio" className="text-xs uppercase tracking-[0.2em] text-[#080808]/70 hover:text-[#080808]">← Estudio</Link>
      </div>
      <p className="mt-3 text-sm text-[#080808]/65">Usa solo imágenes propias o autorizadas. El orden define cómo aparecen en Inicio.</p>
      <div className="mt-8 space-y-6">{data.map((t) => <ThemeEditor key={t.slug} t={t} refresh={() => qc.invalidateQueries({ queryKey: ["inspiration"] })} />)}</div>
    </div>
  );
}
